import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, Send, X } from 'lucide-react';
import { fmtDateTime } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

/** Generic compact 1-to-1 chat: opens (or reuses) a thread with `targetUserId`
 *  via /api/chat/threads and polls messages. Used in the recruiter profile
 *  popup and the team chat widget. */
export default function ProfileChatLite({ targetUserId, currentUserId, pollMs = 5000, heightClass = 'h-72' }) {
  const [thread, setThread] = useState(null);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!targetUserId) { setError('This user has no portal account yet.'); return; }
    let cancelled = false;
    (async () => {
      setError('');
      setThread(null);
      try {
        const res = await fetch(`${API}/api/chat/threads`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ user_id: targetUserId }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          if (!cancelled) setError(d.detail || 'Could not open chat');
          return;
        }
        if (!cancelled) setThread(await res.json());
      } catch {
        if (!cancelled) setError('Network error opening chat');
      }
    })();
    return () => { cancelled = true; };
  }, [targetUserId]);

  const fetchMessages = async () => {
    if (!thread) return;
    try {
      const res = await fetch(`${API}/api/chat/threads/${thread.thread_id}/messages`, { credentials: 'include' });
      if (res.ok) setMessages((await res.json()).messages || []);
      fetch(`${API}/api/chat/threads/${thread.thread_id}/read`, { method: 'POST', credentials: 'include' }).catch(() => {});
    } catch { /* offline */ }
  };

  useEffect(() => {
    if (!thread) return;
    fetchMessages();
    const id = setInterval(fetchMessages, pollMs);
    return () => clearInterval(id);
  }, [thread]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  const send = async () => {
    if (!thread || sending || (!body.trim() && !attachment)) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('body', body.trim());
      if (attachment) fd.append('attachment', attachment);
      const res = await fetch(`${API}/api/chat/threads/${thread.thread_id}/messages`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (res.ok) {
        setBody(''); setAttachment(null);
        await fetchMessages();
      }
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="profile-chat-error">{error}</div>;
  }

  return (
    <div className="flex flex-col" data-testid="profile-chat-lite">
      <div ref={scrollRef} className={`${heightClass} overflow-y-auto bg-tge-cream-soft/40 border border-tge-navy/10 rounded-xl p-3 space-y-2`}>
        {messages.length === 0 && (
          <div className="text-xs text-tge-muted text-center pt-8">No messages yet — say hello!</div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === currentUserId;
          return (
            <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-xs shadow-sm ${mine ? 'bg-tge-navy text-white rounded-br-sm' : 'bg-white text-tge-navy border border-tge-navy/10 rounded-bl-sm'}`}>
                {!mine && <div className="text-[10px] font-semibold opacity-60 mb-0.5">{m.sender_name}</div>}
                {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                {m.attachment_name && (
                  <a
                    href={`${API}/api/chat/threads/${thread.thread_id}/attachments/${m.message_id}`}
                    target="_blank" rel="noreferrer"
                    className={`underline text-[11px] inline-flex items-center gap-1 mt-0.5 ${mine ? 'text-tge-gold' : 'text-tge-navy'}`}
                  >
                    <Paperclip size={10} /> {m.attachment_name}
                  </a>
                )}
                <div className={`text-[9px] mt-0.5 ${mine ? 'text-white/50' : 'text-tge-navy/40'}`}>{fmtDateTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
      {attachment && (
        <div className="flex items-center gap-2 text-[11px] text-tge-navy/70 mt-1.5 bg-tge-cream-soft rounded px-2 py-1">
          <Paperclip size={11} /><span className="truncate flex-1">{attachment.name}</span>
          <button onClick={() => setAttachment(null)}><X size={11} /></button>
        </div>
      )}
      <div className="flex items-center gap-1.5 mt-2">
        <button
          onClick={() => fileRef.current?.click()}
          className="w-9 h-9 rounded-lg border border-tge-navy/15 flex items-center justify-center text-tge-navy/60 hover:bg-tge-cream-soft"
          data-testid="profile-chat-attach"
        >
          <Paperclip size={13} />
        </button>
        <input type="file" ref={fileRef} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f && f.size <= 10 * 1024 * 1024) setAttachment(f); }} />
        <input
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type a message…"
          className="flex-1 h-9 rounded-lg border border-tge-navy/15 px-3 text-xs focus:outline-none focus:border-tge-navy/40"
          data-testid="profile-chat-input"
        />
        <button
          onClick={send}
          disabled={sending || (!body.trim() && !attachment)}
          className="w-9 h-9 rounded-lg bg-tge-navy hover:bg-tge-navy/90 disabled:opacity-40 text-white flex items-center justify-center"
          data-testid="profile-chat-send"
        >
          <Send size={13} />
        </button>
      </div>
    </div>
  );
}
